import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector, useAppStore } from "@/app/appStore";
import {
  cancelAnnotation,
  CartView,
  selectAnnotationView,
  selectCartView,
  selectIsAnnotationSaving,
  selectIsEditable,
  selectIsOrderLoading,
  selectPinBasedPredictions,
  selectAnnotatedCartItems,
  selectZoneBasedPredictions,
  setAnnotationSaving,
  setCartView,
  setComparisonModalVisibility,
  setFirstJourneyIdAsSelected,
  setPinBasedPredictions,
  setZoneBasedPredictions,
  updateEditorMode,
} from "@/entities/annotation";
import {
  ANNOTATION_TYPES,
  CART_VIEWS,
  EDITOR_MODES,
  ANNOTATION_RECORD_STATUSES,
  PREDICTION_TYPES,
} from "@/entities/annotation/model/consts";
import {
  checkJourneysHaveEnterAndExit,
  createJourneyPayload,
  createAnnotatedCartFromTransactions,
  formatTimestamp,
  groupAnnotationRecordsByKeyAndStatus,
  isPinBasedPredictionJson,
  isZoneBasedPredictionJson,
  transformJourneyProximityToRecord,
  transformProximityTimeRangeToRecord,
  transformRecordToPinPredictions,
  transformRecordToZonePredictions,
  transformTrackPersistenceToRecord,
  transformTransactionToRecord,
} from "@/features/annotation/lib/helpers";
import {
  OrderStatus,
  PinBasedPredictionJson,
} from "@/features/annotation/lib/types";
import { displayUUID } from "@/shared";
import {
  Order,
  JourneyEventRequest,
  useCreateJourneysMutation,
  useCreateJourneyTransactionsMutation,
  useCreateTrackPersistencesMutation,
  useDeleteJourneysMutation,
  useDeleteJourneyTransactionsMutation,
  useDeleteTrackPersistencesMutation,
  useUpdateJourneysMutation,
  useUpdateOrderStatusMutation,
  useCreateJourneyProximitiesMutation,
  useDeleteJourneyProximitiesMutation,
  useCreateProximityTimeRangesMutation,
  useDeleteProximityTimeRangesMutation,
} from "@/shared/api/lod-api";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
} from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  ScrollArea,
  SegmentedControl,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { toast } from "react-hot-toast";
import ProductItem from "./product-item";
import AnnotatedCart from "./annotated-cart";
import PredictionCart from "./prediction-cart";
import CompareCarts from "./compare-carts";
import CartIsEmpty from "./cart-is-empty";
import { InfoItem } from "@/components/info-item";
import { PredictionInfoTooltip } from "@/components/prediction-info-tooltip";
import { useCopyToClipboard } from "react-use";
import { PasteFromClipboard } from "./paste-from-clipboard";
import { useTimelineData } from "../hooks/useTimelineData";
import { useSystemTimelineEvent } from "../hooks/useSystemTimelineEvent";
import { AnnotationActionButtons } from "./annotation-action-buttons";
import { AnnotationStageStatusesDialog } from "./annotation-stage-statuses-dialog";
import { StageStatusItem } from "./stage-status-item";
import { RefetchButton } from "./refetch-button";
import { usePredictionCart } from "../hooks/usePredictionCart";
import { useSystemTime } from "../hooks/useSystemTime";
import { LastFetchedInfo } from "./last-fetched-info";
import { CartSection } from "./cart-section";
import { useAnnotatorData } from "@/shared/hooks/useAnnotatorData";
import { STAGE_STATUS } from "@/features/assignment/lib/types";

interface JourneyAnnotation<T> {
  journeyId: string;
  records: T[];
}

interface AnnotationPayload<T> {
  orderId: string | number;
  journeyAnnotations: JourneyAnnotation<T>[];
}
interface SaveRecordsConfig<T extends { id: string | number }, R> {
  records: T[];
  transform: (record: T) => R;
  createRequest: (payload: any) => Promise<any>;
  deleteRequest: (payload: any) => Promise<any>;
  groupByKey?: string;
  statusKey?: string;
  successMessage?: string;
}

export function OrderDetails({
  selectedOrder,
  onFinishAnnotation,
  onSelectOrder,
  onBack,
  onSwitch,
  timerEnabled,
  timerTimeLeft,
  onTimerEnable,
  onTimerDisable,
}: {
  selectedOrder: Order;
  onFinishAnnotation: () => void;
  onSelectOrder: (orderId: string) => void;
  onBack: () => void;
  onSwitch: (direction: "previous" | "next") => void;
  previousOrder?: Order | null;
  nextOrder?: Order | null;
  timerEnabled: boolean;
  timerTimeLeft: number;
  onTimerEnable: () => void;
  onTimerDisable: () => void;
}) {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const annotationView = useAppSelector(selectAnnotationView);
  const cartView = useAppSelector(selectCartView);
  const isEditable = useAppSelector(selectIsEditable);
  const {
    createPinPredictionTimelineGroups,
    createZonePredictionTimelineGroups,
    removePinPredictionTimelineGroups,
    removeZonePredictionTimelineGroups,
  } = useTimelineData();
  const pinBasedPredictions = useAppSelector(selectPinBasedPredictions);
  const zoneBasedPredictions = useAppSelector(selectZoneBasedPredictions);
  const isOrderLoading = useAppSelector(selectIsOrderLoading);
  const isAnnotationSaving = useAppSelector(selectIsAnnotationSaving);
  const [createJourneysRequest, createJourneysResult] =
    useCreateJourneysMutation();
  const [updateJourneysRequest, updateJourneysResult] =
    useUpdateJourneysMutation();
  const [deleteJourneysRequest, deleteJourneysResult] =
    useDeleteJourneysMutation();
  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  const [createTransactionsRequest, createTransactionsResult] =
    useCreateJourneyTransactionsMutation();
  const [deleteTransactionsRequest, deleteTransactionsResult] =
    useDeleteJourneyTransactionsMutation();

  const [createTrackPersistenceRequest, createTrackPersistenceResult] =
    useCreateTrackPersistencesMutation();
  const [deleteTrackPersistenceRequest, deleteTrackPersistenceResult] =
    useDeleteTrackPersistencesMutation();

  const [createJourneyProximityRequest, createJourneyProximityResult] =
    useCreateJourneyProximitiesMutation();
  const [deleteJourneyProximityRequest, deleteJourneyProximityResult] =
    useDeleteJourneyProximitiesMutation();

  const [createProximityTimeRangesRequest, createProximityTimeRangesResult] =
    useCreateProximityTimeRangesMutation();
  const [deleteProximityTimeRangesRequest, deleteProximityTimeRangesResult] =
    useDeleteProximityTimeRangesMutation();

  useSystemTimelineEvent();

  const {
    data: predictionCartData,
    isLoading: isPredictionLoading,
    isFetching: isPredictionFetching,
    timeElapsed: predictionTimeElapsed,
    formattedTimeElapsed: formattedPredictionTimeElapsed,
    error: predictionError,
    refetch: refetchPredictionCart,
    journeyAnalysis,
  } = usePredictionCart();

  const { annotatorLookup } = useAnnotatorData();

  const { refetch: refetchSystemTime } = useSystemTime();

  const refetchPredictionCartAndSystemTime = useCallback(() => {
    refetchPredictionCart();
    refetchSystemTime();
  }, [refetchPredictionCart, refetchSystemTime]);

  const isRequestLoading =
    createJourneysResult.isLoading ||
    updateJourneysResult.isLoading ||
    deleteJourneysResult.isLoading ||
    createTransactionsResult.isLoading ||
    deleteTransactionsResult.isLoading ||
    createTrackPersistenceResult.isLoading ||
    deleteTrackPersistenceResult.isLoading ||
    createJourneyProximityResult.isLoading ||
    deleteJourneyProximityResult.isLoading ||
    createProximityTimeRangesResult.isLoading ||
    deleteProximityTimeRangesResult.isLoading;

  const hasOrderLines = selectedOrder?.lines?.length > 0;

  const rejectionReason = selectedOrder?.stage_detail_statuses?.find(
    (stage) => stage.status === STAGE_STATUS.REJECTED && stage.rejection_reason
  )?.rejection_reason;

  const saveJourneys = async () => {
    try {
      const state = store.getState();
      const { journeys, events, journeyTransactions } = state.annotation;

      const haveEnterAndExitEvents = checkJourneysHaveEnterAndExit(
        events,
        journeyTransactions,
        journeys
      );

      if (!haveEnterAndExitEvents) {
        toast.error("Make sure you annotated both enter and exit events");
        throw new Error("Make sure you annotated both enter and exit events");
      }

      const createdJourneys: JourneyEventRequest[] = [];
      const updatedJourneys: { id: string; journey: JourneyEventRequest }[] =
        [];
      const deletedJourneys: string[] = [];

      journeys.forEach((journey) => {
        if (journey.status === ANNOTATION_RECORD_STATUSES.DELETED) {
          deletedJourneys.push(journey.id);
        } else {
          const enterEvent = events.find(
            (event) =>
              event.journeyId === journey.id && event.eventType === "enter"
          );
          const exitEvent = events.find(
            (event) =>
              event.journeyId === journey.id && event.eventType === "exit"
          );

          if (!enterEvent || !exitEvent) {
            toast.error(
              `Missing enter or exit event for journeyId: ${journey.id}`
            );
            throw new Error(
              `Missing enter or exit event for journeyId: ${journey.id}`
            );
          }

          const payload = createJourneyPayload({
            enterEvent,
            exitEvent,
            note: journey.note || "",
          });

          if (journey.status === ANNOTATION_RECORD_STATUSES.NEW) {
            createdJourneys.push(payload);
          } else if (journey.status === ANNOTATION_RECORD_STATUSES.MODIFIED) {
            updatedJourneys.push({ journey: payload, id: journey.id });
          }
        }
      });

      if (createdJourneys.length > 0) {
        await createJourneysRequest({
          orderId: selectedOrder.id,
          journeys: createdJourneys,
        });
      }
      if (updatedJourneys.length > 0) {
        await updateJourneysRequest({
          orderId: selectedOrder.id,
          journeys: updatedJourneys,
        });
      }
      if (deletedJourneys.length > 0) {
        await deleteJourneysRequest({
          orderId: selectedOrder.id,
          journeyIds: deletedJourneys,
        });
      }

      await updateOrderStatus({
        orderId: selectedOrder.id,
        status: OrderStatus.Finished,
      });

      toast.success("Journey updated successfully");
      dispatch(updateEditorMode(EDITOR_MODES.READ_ONLY));
      dispatch(cancelAnnotation());
      onFinishAnnotation();
      onSelectOrder(selectedOrder.id);
      onTimerEnable();
    } catch (error: any) {
      console.error("Failed to save journeys:", error);
      toast.error("Failed to save journeys. Please try again.");
    } finally {
      dispatch(setAnnotationSaving(false));
    }
  };

  const saveRecordsHelper = async <
    T extends { id: string | number; [key: string]: any },
    R,
  >(
    config: SaveRecordsConfig<T, R>
  ): Promise<void> => {
    const {
      records,
      transform,
      createRequest,
      deleteRequest,
      groupByKey = "journeyId",
      statusKey = "status",
      successMessage = "Records saved successfully",
    } = config;

    const { created, deleted } = groupAnnotationRecordsByKeyAndStatus(
      records,
      transform,
      groupByKey,
      statusKey
    );

    try {
      const sendCreateRequests = async (): Promise<void> => {
        const groupEntries = Object.entries(created);

        if (groupEntries.length > 0) {
          const payload: AnnotationPayload<R> = {
            orderId: selectedOrder?.id,
            journeyAnnotations: groupEntries.map(([groupId, items]) => ({
              journeyId: groupId,
              records: items,
            })),
          };

          await createRequest(payload);
        }
      };

      const sendDeleteRequests = async (): Promise<void> => {
        const groupEntries = Object.entries(deleted);

        if (groupEntries.length > 0) {
          const payload: AnnotationPayload<R & { id: string | number }> = {
            orderId: selectedOrder?.id,
            journeyAnnotations: groupEntries.map(([groupId, items]) => ({
              journeyId: groupId,
              records: items,
            })),
          };

          await deleteRequest(payload);
        }
      };

      await sendCreateRequests();
      await sendDeleteRequests();

      toast.success(successMessage);
      dispatch(updateEditorMode(EDITOR_MODES.READ_ONLY));
      dispatch(cancelAnnotation());
      onFinishAnnotation();
      onSelectOrder(selectedOrder.id);
      onTimerEnable();
    } catch (error: any) {
      toast.error(`Failed to save`);
      console.error(`Save failed:`, error);
    } finally {
      dispatch(setAnnotationSaving(false));
    }
  };

  const saveTransactions = () => {
    const state = store.getState();
    return saveRecordsHelper({
      records: state.annotation.journeyTransactions,
      transform: transformTransactionToRecord,
      createRequest: createTransactionsRequest,
      deleteRequest: deleteTransactionsRequest,
      successMessage: "Journey Transaction updated successfully",
    });
  };

  const saveTrackPersistences = () => {
    const state = store.getState();
    return saveRecordsHelper({
      records: state.annotation.trackPersistences,
      transform: transformTrackPersistenceToRecord,
      createRequest: createTrackPersistenceRequest,
      deleteRequest: deleteTrackPersistenceRequest,
      successMessage: "Track Persistence updated successfully",
    });
  };

  const saveJourneyProximities = () => {
    const state = store.getState();
    return saveRecordsHelper({
      records: state.annotation.journeyProximities,
      transform: transformJourneyProximityToRecord,
      createRequest: createJourneyProximityRequest,
      deleteRequest: deleteJourneyProximityRequest,
      successMessage: "Proximity updated successfully",
    });
  };

  const saveProximityTimeRanges = () => {
    const state = store.getState();
    return saveRecordsHelper({
      records: state.annotation.proximityTimeRanges,
      transform: transformProximityTimeRangeToRecord,
      createRequest: createProximityTimeRangesRequest,
      deleteRequest: deleteProximityTimeRangesRequest,
      successMessage: "Proximity Time Ranges updated successfully",
    });
  };

  const saveRecords = () => {
    dispatch(setAnnotationSaving(true));
    switch (annotationView) {
      case ANNOTATION_TYPES.JOURNEY:
        saveJourneys();
        break;
      case ANNOTATION_TYPES.TRANSACTION:
        saveTransactions();
        break;
      case ANNOTATION_TYPES.TRACK_PERSISTENCE:
        saveTrackPersistences();
        break;
      case ANNOTATION_TYPES.PROXIMITY:
        saveJourneyProximities();
        break;
      case ANNOTATION_TYPES.PROXIMITY_TIME_RANGES:
        saveProximityTimeRanges();
        break;
      default:
        break;
    }
  };

  const onUpdate = () => {
    dispatch(updateEditorMode(EDITOR_MODES.EDIT));
    dispatch(setFirstJourneyIdAsSelected());
    onTimerDisable();
  };

  const onCancel = () => {
    dispatch(cancelAnnotation());
    onTimerDisable();
    onFinishAnnotation();
    onSelectOrder(selectedOrder.id);
  };

  const onFinish = () => {
    saveRecords();
  };

  const onSwitchToPreviousOrder = () => {
    onSwitch("previous");
  };

  const onSwitchToNextOrder = () => {
    onSwitch("next");
  };

  const onCartViewChange = (value: CartView) => {
    dispatch(setCartView(value));
  };

  const handleSegmentedControlFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    e.target.blur();
  };

  const renderActionButtons = () => {
    return (
      <AnnotationActionButtons
        isEditable={isEditable}
        isRequestLoading={isRequestLoading}
        isOrderLoading={isOrderLoading}
        isAnnotationSaving={isAnnotationSaving}
        onCancel={onCancel}
        onUpdate={onUpdate}
        onFinish={onFinish}
      />
    );
  };

  const [_copiedState, copyToClipboard] = useCopyToClipboard();

  const onCopyAnnotatedCart = () => {
    const state = store.getState();
    const journeyTransactions = selectAnnotatedCartItems(state);
    const annotatedCart =
      createAnnotatedCartFromTransactions(journeyTransactions);

    copyToClipboard(
      JSON.stringify(
        annotatedCart.map(({ barcode, quantity, uom }) => ({
          barcode,
          quantity,
          uom,
        }))
      )
    );
    toast.success("Annotated cart is copied to clipboard");
  };

  const handleImportedData = (data: unknown) => {
    if (!data || typeof data !== "object") {
      throw new Error("Invalid data structure");
    }

    const type = (data as PinBasedPredictionJson).result?.type;
    const orderId = (data as PinBasedPredictionJson).result?.order_id;

    if (orderId !== selectedOrder.id) {
      toast.error(
        "JSON data not imported: File belongs to a different order. Please check and try again."
      );
      return;
    }

    if (type === PREDICTION_TYPES.PIN) {
      if (!isPinBasedPredictionJson(data)) {
        toast.error("Invalid pin based predictions JSON structure");
        return;
      }
      removePinPredictionTimelineGroups(pinBasedPredictions);

      const newPredictions = transformRecordToPinPredictions(data);
      createPinPredictionTimelineGroups(newPredictions);

      dispatch(setPinBasedPredictions(newPredictions));
      toast.success("Pin based prediction data imported successfully");
    } else if (type === PREDICTION_TYPES.ZONE) {
      if (!isZoneBasedPredictionJson(data)) {
        toast.error("Invalid zone based predictions JSON structure");
        return;
      }
      removeZonePredictionTimelineGroups(zoneBasedPredictions);

      const newPredictions = transformRecordToZonePredictions(data);
      createZonePredictionTimelineGroups(newPredictions);

      dispatch(setZoneBasedPredictions(newPredictions));
      toast.success("Zone based prediction data imported successfully");
    } else {
      toast.error("Invalid prediction type");
      return;
    }
  };

  return (
    <Flex direction="column" maxHeight="100%" height="100%">
      <CompareCarts />
      <Flex px="2" py="1" gap="0" align="center" className="border-b">
        <Button
          size="1"
          variant="ghost"
          color="gray"
          className="m-0"
          disabled={isEditable || isOrderLoading || isAnnotationSaving}
          onClick={onBack}
        >
          <ChevronLeftIcon />
          <Text>Orders</Text>
        </Button>
        <div className="grow" />

        <PasteFromClipboard onSave={handleImportedData} />

        {renderActionButtons()}
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          className="m-0"
          disabled
          onClick={onSwitchToPreviousOrder}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          className="m-0"
          disabled
          onClick={onSwitchToNextOrder}
        >
          <ChevronRightIcon />
        </IconButton>
      </Flex>

      <Box className="p-2 border-b">
        <Text size="2" mb="1" as="p" weight="bold">
          Order Details
        </Text>

        <InfoItem
          label="Order ID"
          value={selectedOrder.id ? displayUUID(selectedOrder.id) : undefined}
          copyValue={selectedOrder.id || undefined}
        />
        <InfoItem
          label="Order creation time"
          value={selectedOrder?.time && formatTimestamp(selectedOrder?.time)}
          copyValue={selectedOrder?.time}
        />
        <InfoItem
          label="Current Assignee"
          value={
            selectedOrder.annotator_id
              ? annotatorLookup[selectedOrder.annotator_id] ||
                displayUUID(selectedOrder.annotator_id)
              : "-"
          }
          copyValue={selectedOrder.annotator_id || undefined}
        />
        <InfoItem label="Source" value={selectedOrder.order_source} />
        {selectedOrder.stage_detail_statuses?.map((status) => (
          <StageStatusItem
            key={status.stage}
            label={status.stage}
            value={status.status}
          />
        ))}

        {rejectionReason && (
          <InfoItem label="Rejection Reason" value={rejectionReason} />
        )}

        {selectedOrder?.anomaly_report ? (
          <Flex direction="column" gap="1">
            <Text size="1" color="gray">
              Anomaly Report:
            </Text>
            <TextArea
              rows={3}
              readOnly
              value={selectedOrder.anomaly_report}
              style={{ resize: "none" }}
            />
          </Flex>
        ) : (
          <InfoItem label="Anomaly Report" value="-" copyValue="-" />
        )}

        <AnnotationStageStatusesDialog
          order={selectedOrder}
          enabled={timerEnabled}
          timeLeft={timerTimeLeft}
          onSaved={() => {
            onTimerDisable();
            onSelectOrder(selectedOrder.id);
          }}
        />
      </Box>
      <Flex justify="between" align="center" p="2" gap="2">
        <Text size="2" weight="bold">
          Cart items:
        </Text>
        <Button
          size="1"
          variant="soft"
          color="gray"
          onClick={() => dispatch(setComparisonModalVisibility(true))}
        >
          Compare carts
        </Button>
      </Flex>
      <Flex p="2" gap="2" align="center">
        <Flex align="center" gap="2">
          <SegmentedControl.Root
            size="1"
            value={cartView}
            onValueChange={onCartViewChange}
            onFocus={handleSegmentedControlFocus}
          >
            <SegmentedControl.Item value={CART_VIEWS.ANNOTATED}>
              Annotated
            </SegmentedControl.Item>
            <SegmentedControl.Item value={CART_VIEWS.ORDER}>
              <Box className="relative">
                Order
                {hasOrderLines && (
                  <Box className="absolute -top-0.5 -right-6 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </Box>
            </SegmentedControl.Item>
            <SegmentedControl.Item value={CART_VIEWS.PREDICTION}>
              Predicted
            </SegmentedControl.Item>
          </SegmentedControl.Root>

          {cartView === CART_VIEWS.ANNOTATED && (
            <Tooltip
              content="Copy annotated cart items to clipboard in JSON format"
              delayDuration={1200}
            >
              <IconButton
                size="1"
                variant="soft"
                color="gray"
                onClick={onCopyAnnotatedCart}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          )}
          {cartView === CART_VIEWS.PREDICTION && <PredictionInfoTooltip />}
        </Flex>
        {cartView === CART_VIEWS.PREDICTION && (
          <Flex justify="end" width="100%" pr="2">
            <RefetchButton
              timeElapsed={formattedPredictionTimeElapsed}
              isPredictionFetching={isPredictionFetching}
              onRefetch={refetchPredictionCartAndSystemTime}
            />
          </Flex>
        )}
      </Flex>
      {cartView === CART_VIEWS.ORDER ? (
        <CartSection>
          <OrderProducts selectedOrder={selectedOrder} />
        </CartSection>
      ) : cartView === CART_VIEWS.PREDICTION ? (
        <CartSection
          headerContent={
            <LastFetchedInfo
              isStale={predictionTimeElapsed > 30_000}
              timeElapsed={formattedPredictionTimeElapsed}
              hasNoJourneys={journeyAnalysis.hasNoJourneys}
            />
          }
        >
          <PredictionCart
            predictionCartData={predictionCartData}
            isPredictionLoading={isPredictionLoading}
            predictionError={predictionError}
          />
        </CartSection>
      ) : (
        <CartSection>
          <AnnotatedCart />
        </CartSection>
      )}
    </Flex>
  );
}

function OrderProducts({ selectedOrder }: { selectedOrder: Order }) {
  const sortedOrderLines = useMemo(() => {
    if (!selectedOrder?.lines?.length) return [];
    return [...selectedOrder.lines].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [selectedOrder?.lines]);

  return (
    <ScrollArea type="auto" className="h-0 flex-grow">
      <Flex direction="column" gap="2" className="h-0 flex-grow" px="2">
        {sortedOrderLines.length ? (
          sortedOrderLines.map(
            ({
              name,
              image_url: imageUrl,
              barcode,
              sku_base_id: skuId,
              quantity,
              uom,
            }) => (
              <ProductItem
                key={skuId}
                name={name}
                imageUrl={imageUrl}
                barcode={barcode}
                skuId={skuId}
                quantity={quantity}
                isQuantityNegative={quantity < 0}
                unitOfMeasurement={uom}
              />
            )
          )
        ) : (
          <CartIsEmpty />
        )}
      </Flex>
    </ScrollArea>
  );
}
